import React, { useState, useEffect } from "react";
import axios from "../../AxiosInstance";
import {
  Tab,
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  Spinner,
} from "react-bootstrap";
import Sidebar from "../../components/Sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const AdminMenuManagerLanguage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine if we're on an individual page (should hide tabs)
  const isIndividualPage = location.pathname.includes("age-ranges") || 
                          location.pathname.includes("languages");
  
  // Determine active tab based on route
  const getInitialTab = () => {
    if (location.pathname.includes("age-ranges")) return "ageRanges";
    if (location.pathname.includes("languages")) return "languages";
    return "ageRanges"; // default
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [ageRanges, setAgeRanges] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    label: "",
    minAge: 0,
    maxAge: 0,
    name: "",
    code: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  // Update active tab when route changes
  useEffect(() => {
    if (location.pathname.includes("age-ranges")) {
      setActiveTab("ageRanges");
    } else if (location.pathname.includes("languages")) {
      setActiveTab("languages");
    } else {
      setActiveTab("ageRanges");
    }
  }, [location.pathname]);

  // API Functions
  const fetchAgeRanges = async () => {
    try {
      const response = await axios.get(`api/v1/stores/age`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return response.data.data;
    } catch (error) {
      console.error(
        "Error fetching age ranges:",
        error.response?.data?.message || error.message
      );
      throw error;
    }
  };

  const createAgeRange = async (ageRangeData) => {
    try {
      const response = await axios.post(
        `api/v1/stores/age`,
        ageRangeData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error(
        "Error creating age range:",
        error.response?.data?.message || error.message
      );
      throw error;
    }
  };

  const updateAgeRange = async (id, ageRangeData) => {
    try {
      const response = await axios.put(
        `api/v1/stores/age/${id}`,
        ageRangeData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error(
        "Error updating age range:",
        error.response?.data?.message || error.message
      );
      throw error;
    }
  };

  const deleteAgeRange = async (id) => {
    try {
      await axios.delete(`api/v1/stores/age/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
    } catch (error) {
      console.error(
        "Error deleting age range:",
        error.response?.data?.message || error.message
      );
      throw error;
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`api/v1/stores/lang`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return response.data.data;
    } catch (error) {
      console.error(
        "Error fetching languages:",
        error.response?.data?.message || error.message
      );
      throw error;
    }
  };

  const createLanguage = async (languageData) => {
    try {
      const response = await axios.post(
        `api/v1/stores/lang`,
        languageData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error(
        "Error creating language:",
        error.response?.data?.message || error.message
      );
      throw error;
    }
  };

  const updateLanguage = async (id, languageData) => {
    try {
      const response = await axios.put(
        `api/v1/stores/lang/${id}`,
        languageData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error(
        "Error updating language:",
        error.response?.data?.message || error.message
      );
      throw error;
    }
  };

  const deleteLanguage = async (id) => {
    try {
      await axios.delete(`api/v1/stores/lang/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
    } catch (error) {
      console.error(
        "Error deleting language:",
        error.response?.data?.message || error.message
      );
      throw error;
    }
  };

  // Component Logic
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        if (activeTab === "ageRanges") {
          const data = await fetchAgeRanges();
          setAgeRanges(data);
        } else {
          const data = await fetchLanguages();
          setLanguages(data);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || "Failed to fetch data";
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [activeTab]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (activeTab === "ageRanges") {
        if (currentItem) {
          const updated = await updateAgeRange(currentItem._id, formData);
          setAgeRanges(
            ageRanges.map((item) => (item._id === updated._id ? updated : item))
          );
          toast.success("Age range updated successfully");
        } else {
          const created = await createAgeRange(formData);
          setAgeRanges([...ageRanges, created]);
          toast.success("Age range created successfully");
        }
      } else {
        if (currentItem) {
          const updated = await updateLanguage(currentItem._id, formData);
          setLanguages(
            languages.map((item) => (item._id === updated._id ? updated : item))
          );
          toast.success("Language updated successfully");
        } else {
          const created = await createLanguage(formData);
          setLanguages([...languages, created]);
          toast.success("Language created successfully");
        }
      }
      setShowModal(false);
      setFormData({
        label: "",
        minAge: 0,
        maxAge: 0,
        name: "",
        code: "",
      });
      setCurrentItem(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Operation failed";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (item) => {
    setCurrentItem(item);
    if (activeTab === "ageRanges") {
      setFormData({
        label: item.label,
        minAge: item.minAge,
        maxAge: item.maxAge,
        name: "",
        code: "",
      });
    } else {
      setFormData({
        name: item.name,
        code: item.code,
        label: "",
        minAge: 0,
        maxAge: 0,
      });
    }
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      setIsLoading(true);
      try {
        if (activeTab === "ageRanges") {
          await deleteAgeRange(id);
          setAgeRanges(ageRanges.filter((item) => item._id !== id));
          toast.success("Age range deleted successfully");
        } else {
          await deleteLanguage(id);
          setLanguages(languages.filter((item) => item._id !== id));
          toast.success("Language deleted successfully");
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || "Failed to delete";
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleAddClick = () => {
    setCurrentItem(null);
    setShowModal(true);
    setFormData({
      label: "",
      minAge: 0,
      maxAge: 0,
      name: "",
      code: "",
    });
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col p-4 overflow-hidden">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex-shrink-0">
          Menu Management
        </h2>

        {isIndividualPage ? (
          // Show only the relevant content for individual pages
          activeTab === "ageRanges" ? (
            <>
              {isLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-auto min-h-0">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Label</th>
                          <th>Age Range</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ageRanges.map((range) => (
                          <tr key={range._id}>
                            <td>{range.label}</td>
                            <td>
                              {range.minAge} - {range.maxAge}
                            </td>
                            <td>
                              <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEdit(range)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(range._id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <div className="mt-3 flex-shrink-0">
                    <Button variant="success" onClick={handleAddClick}>
                      Add Age Range
                    </Button>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {isLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-auto min-h-0">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {languages.map((lang) => (
                          <tr key={lang._id}>
                            <td>{lang.name}</td>
                            <td>{lang.code}</td>
                            <td>
                              <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEdit(lang)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(lang._id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <div className="mt-3 flex-shrink-0">
                    <Button variant="success" onClick={handleAddClick}>
                      Add Language
                    </Button>
                  </div>
                </>
              )}
            </>
          )
        ) : (
          // Show tabs for main page
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k);
              // Don't navigate on /admin/language - just switch tabs
            }}
            className="mb-3"
          >
            <Tab eventKey="ageRanges" title="Age Ranges">
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 300px)" }}>
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Label</th>
                          <th>Age Range</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ageRanges.map((range) => (
                          <tr key={range._id}>
                            <td>{range.label}</td>
                            <td>
                              {range.minAge} - {range.maxAge}
                            </td>
                            <td>
                              <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEdit(range)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(range._id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <Button variant="success" className="mt-3" onClick={handleAddClick}>
                    Add Age Range
                  </Button>
                </>
              )}
            </Tab>

            <Tab eventKey="languages" title="Languages">
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 300px)" }}>
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {languages.map((lang) => (
                          <tr key={lang._id}>
                            <td>{lang.name}</td>
                            <td>{lang.code}</td>
                            <td>
                              <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEdit(lang)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(lang._id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <Button variant="success" className="mt-3" onClick={handleAddClick}>
                    Add Language
                  </Button>
                </>
              )}
            </Tab>
          </Tabs>
        )}

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>
              {currentItem ? "Edit" : "Add"}{" "}
              {activeTab === "ageRanges" ? "Age Range" : "Language"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              {activeTab === "ageRanges" ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Label</Form.Label>
                    <Form.Control
                      type="text"
                      name="label"
                      value={formData.label}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Minimum Age</Form.Label>
                    <Form.Control
                      type="number"
                      name="minAge"
                      value={formData.minAge}
                      onChange={handleInputChange}
                      required
                      min="0"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Maximum Age</Form.Label>
                    <Form.Control
                      type="number"
                      name="maxAge"
                      value={formData.maxAge}
                      onChange={handleInputChange}
                      required
                      min={formData.minAge}
                    />
                  </Form.Group>
                </>
              ) : (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Language Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Language Code (ISO)</Form.Label>
                    <Form.Control
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      required
                      maxLength="2"
                      placeholder="en, es, fr, etc."
                    />
                  </Form.Group>
                </>
              )}
              <Button variant="primary" type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save"}
              </Button>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default AdminMenuManagerLanguage;
